import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { teacherStopDetail, markNotifReviewed } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, AlertCircle, CheckCircle2 } from "lucide-react";

export default function TeacherStopAlert() {
  const { stopId } = useParams();
  const [data, setData] = useState(null);

  const refresh = async () => setData(await teacherStopDetail(stopId));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [stopId]);

  const onMarkReviewed = async () => {
    if (data?.notification?.notif_id) {
      await markNotifReviewed(data.notification.notif_id);
      refresh();
    }
  };

  if (!data) return <div className="text-center py-20 text-slate-400" data-testid="stop-alert-loading">Loading…</div>;

  const { stop, learner, trigger_questions, history, notification } = data;

  return (
    <div className="teacher-scope min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="teacher-stop-alert">
        <Link to="/teacher" className="inline-flex items-center gap-1 text-slate-700 font-semibold hover:underline mb-4 tap-44" data-testid="back-teacher-dash">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-amber-700" />
          </div>
          <div className="flex-1">
            <h1 className="font-teacher text-2xl md:text-3xl font-bold text-slate-900" data-testid="stop-alert-headline">
              STOP fired — {learner?.name}
            </h1>
            <p className="text-slate-600 mt-1">
              {stop.subject} • <strong>{stop.sub_topic}</strong> • triggered in <strong>{stop.trigger_layer}</strong> layer
            </p>
            <p className="text-xs text-slate-500 mt-1">{new Date(stop.created_at).toLocaleString()}</p>
          </div>
          {notification && !notification.is_reviewed && (
            <Button onClick={onMarkReviewed} className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white" data-testid="mark-reviewed-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Mark reviewed
            </Button>
          )}
          {notification?.is_reviewed && (
            <span className="px-3 py-1 rounded-md bg-emerald-100 text-emerald-800 text-xs font-bold" data-testid="reviewed-tag">Reviewed</span>
          )}
        </div>

        <Card className="rounded-xl p-5 bg-white border border-slate-200 mb-6">
          <h2 className="font-teacher font-semibold text-slate-900 mb-3">Reroute path</h2>
          <p className="text-slate-700">
            Type: <strong>{stop.reroute_type}</strong> → <strong>{stop.rerouted_to}</strong>
          </p>
        </Card>

        <h2 className="font-teacher text-xl font-semibold text-slate-900 mb-3">Trigger questions ({trigger_questions.length})</h2>
        <div className="space-y-4 mb-8" data-testid="trigger-questions-list">
          {trigger_questions.map((tq, i) => (
            <Card key={i} className="rounded-xl p-5 bg-white border border-amber-200" data-testid={`trigger-q-${i}`}>
              <div className="flex flex-wrap gap-2 mb-3 text-xs">
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">{tq.record_id}</span>
                <span className="px-2 py-0.5 rounded bg-sky-100 text-sky-800 font-semibold">{tq.difficulty}</span>
                <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">Foundational</span>
                {tq.error_type && <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 font-semibold">Err: {tq.error_type}</span>}
              </div>
              <p className="text-slate-900 font-semibold mb-3">{tq.question_text}</p>
              {tq.options && Array.isArray(tq.options) && tq.options.length > 0 && (
                <ul className="space-y-1 mb-3 text-sm">
                  {tq.options.map((opt, j) => (
                    <li key={j} className={`px-3 py-2 rounded ${opt === (tq.correct_answer_text || tq.correct_answer) ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-slate-50 text-slate-700"}`}>
                      {opt}
                      {opt === (tq.correct_answer_text || tq.correct_answer) && <span className="ml-2 text-xs font-bold">✓ correct</span>}
                    </li>
                  ))}
                </ul>
              )}
              {tq.learner_attempt && (
                <div className="text-sm bg-amber-50 border border-amber-200 rounded p-3" data-testid={`learner-attempt-${i}`}>
                  <strong>{learner?.name} answered:</strong> "{tq.learner_attempt.answer_given}" (wrong)
                </div>
              )}
              {tq.explanation && (
                <details className="mt-3 text-sm">
                  <summary className="cursor-pointer text-sky-700 font-semibold">Explanation</summary>
                  <p className="mt-2 text-slate-700 whitespace-pre-line">{tq.explanation}</p>
                </details>
              )}
            </Card>
          ))}
        </div>

        <h2 className="font-teacher text-xl font-semibold text-slate-900 mb-3">Full attempt history ({history.length})</h2>
        <Card className="rounded-xl bg-white border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold text-slate-700">When</th>
                <th className="px-3 py-2 font-semibold text-slate-700">Layer</th>
                <th className="px-3 py-2 font-semibold text-slate-700">Q</th>
                <th className="px-3 py-2 font-semibold text-slate-700">Answer</th>
                <th className="px-3 py-2 font-semibold text-slate-700">Result</th>
                <th className="px-3 py-2 font-semibold text-slate-700">Diff</th>
              </tr>
            </thead>
            <tbody data-testid="history-table">
              {history.map((h, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-slate-500 text-xs">{new Date(h.created_at).toLocaleTimeString()}</td>
                  <td className="px-3 py-2 text-slate-700">{h.layer}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-600">{h.record_id}</td>
                  <td className="px-3 py-2 text-slate-700">{h.answer_given}</td>
                  <td className={`px-3 py-2 font-semibold ${h.is_correct ? "text-emerald-700" : "text-amber-700"}`}>{h.is_correct ? "✓" : "✕"}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{h.difficulty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
